// src/pages/Plan/PlanLanding.jsx - COMPLETE FILE WITHOUT PROGRESS TEXT
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { planService } from '../../services/planService';
import './PlanLanding.css';

// Custom SVG Icons
const TargetIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

const WalletIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
  </svg>
);

const LockIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const BellIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const PlanLanding = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  const features = [
    {
      icon: <TargetIcon />,
      title: 'Goal-Oriented Planning',
      description: 'Set clear financial goals like buying assets, property, education, or emergency funds with structured investment plans.'
    },
    {
      icon: <WalletIcon />,
      title: 'Automated Savings',
      description: 'Choose periodic contributions that automatically deduct from your wallet every 4 weeks. Set it and forget it!'
    },
    {
      icon: <LockIcon />,
      title: 'Locked Investments',
      description: 'Your funds are locked until maturity to prevent impulsive withdrawals and ensure you reach your goals.'
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Risk-Profiled Strategy',
      description: 'Get personalized investment recommendations based on your income stability, emergency funds, and risk tolerance.'
    },
    {
      icon: <ClockIcon />,
      title: 'Time-Bound Goals',
      description: 'Set investment horizons from 4 weeks to 5+ years with appropriate lock periods for each timeframe.'
    },
    {
      icon: <BellIcon />,
      title: 'Smart Reminders',
      description: 'Receive notifications for plan maturity, due contributions, and progress updates to keep you on track.'
    }
  ];

  const planTypes = [
    {
      type: 'PERIODIC AUTOMATIC',
      description: 'Funds automatically deducted every 4 weeks - perfect for consistent, disciplined saving',
      bestFor: 'Busy professionals, consistent savers'
    },
    {
      type: 'REMINDER-BASED', 
      description: 'Get regular reminders to manually add funds - great for flexible contributions',
      bestFor: 'Flexible savers, irregular income'
    },
    {
      type: 'ONE-TIME INVESTMENT',
      description: 'Single investment with no additional contributions - ideal for lump sum investments',
      bestFor: 'Windfall savings, specific goal funding'
    }
  ];

  const handleContinue = async () => {
    setChecking(true);
    
    try {
      const result = await planService.checkPlanEligibility();
      
      if (result.can_create) {
        navigate('/plan/questionnaire');
      } else {
        alert(result.reason);
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error checking eligibility:', err);
      alert('Failed to check plan eligibility. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="plan-landing">
      {/* Hero Section */}
      <div className="plan-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Smart Investment Planning</h1>
            <p className="hero-subtitle">
              Build wealth systematically with personalized, automated investment plans tailored to your financial goals
            </p>
          </div>
          <div className="hero-visual">
            <div className="visual-card">
              <div className="card-header">
                <span className="card-badge">Active Plan</span>
                <span className="card-amount">₦250,000</span>
              </div>
              <div className="card-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '65%' }}></div>
                </div>
              </div>
              <div className="card-details">
                <div className="detail">
                  <span>Target:</span>
                  <span>₦385,000</span>
                </div>
                <div className="detail">
                  <span>Matures:</span>
                  <span>Dec 2024</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="stat">
            <div className="stat-number">₦2.5B+</div>
            <div className="stat-label">Total Planned</div>
          </div>
          <div className="stat">
            <div className="stat-number">15K+</div>
            <div className="stat-label">Active Plans</div>
          </div>
          <div className="stat">
            <div className="stat-number">94%</div>
            <div className="stat-label">Success Rate</div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="section how-it-works">
        <h2>How Investment Planning Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Set Your Goal</h3>
              <p>Define what you're saving for and your target amount</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Complete Questionnaire</h3>
              <p>Answer 5 simple questions about your financial situation</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Get Personalized Plan</h3>
              <p>Receive risk profile and investment strategy</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Start Investing</h3>
              <p>Fund your plan and watch your savings grow</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="section features">
        <h2>Why Choose Our Investment Plans?</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">
                {feature.icon}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Plan Types */}
      <div className="section plan-types">
        <h2>Choose Your Contribution Style</h2>
        <div className="types-grid">
          {planTypes.map((plan, index) => (
            <div key={index} className="type-card">
              <h3>{plan.type}</h3>
              <p>{plan.description}</p>
              <div className="best-for">
                <strong>Best for:</strong> {plan.bestFor}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="section faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>Can I withdraw my money before maturity?</h3>
            <p>Plans are locked until maturity to help you stay disciplined. Early withdrawals are not permitted except under exceptional circumstances with penalties.</p>
          </div>
          <div className="faq-item">
            <h3>What happens if I miss a periodic payment?</h3>
            <p>For automatic plans, we'll retry for 3 days. If unsuccessful, you'll be notified and the contribution will be skipped for that period.</p>
          </div>
          <div className="faq-item">
            <h3>How is my risk profile determined?</h3>
            <p>Based on your income stability, emergency funds, investment goals, and time horizon through our smart questionnaire.</p>
          </div>
          <div className="faq-item">
            <h3>What happens when my plan matures?</h3>
            <p>You'll receive a notification and can choose to withdraw funds, renew the plan, or create a new one with different goals.</p>
          </div>
          <div className="faq-item">
            <h3>Can I have multiple active plans?</h3>
            <p>No, you can only have one active plan at a time. This ensures focused, disciplined investing toward your primary goal. Complete or withdraw from your current plan before starting a new one.</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="section cta">
        <div className="cta-content">
          <h2>Ready to Start Your Investment Journey?</h2>
          <p>Join thousands of Nigerians building wealth through disciplined, automated investing</p>
          <button 
            className="cta-button" 
            onClick={handleContinue}
            disabled={checking}
          >
            {checking ? 'Checking eligibility...' : 'Create Your Investment Plan'}
          </button>
          <div className="cta-note">
            <span>⏰ Takes just 2 minutes to set up</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanLanding;